<template>
  <div class="row challenge-campaigns-page">
    <div class="col-xl-12 col-md-12 col-xs-12">
      <div class="card mar-to-15">
        <div class="card-content" style="position: relative;">
          <div :class="(loading ? 'show-loader' : 'hide-loader')">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>

          <div class="challenge-campaigns-page__toolbar">
            <div class="challenge-campaigns-page__filters">
              <div class="form-group challenge-campaigns-page__search">
                <label>{{ $t('challengeCampaigns.search') }}</label>
                <el-input
                  v-model="search"
                  :placeholder="$t('challengeCampaigns.search')"
                  size="small"
                  clearable
                  @keyup.enter.native="applyFilters"
                />
              </div>
              <div class="form-group challenge-campaigns-page__status">
                <label>{{ $t('orders.status') }}</label>
                <el-select
                  v-model="statusFilter"
                  clearable
                  size="small"
                  :placeholder="$t('general.select')"
                  style="width: 100%;"
                >
                  <el-option :label="$t('general.active')" :value="1" />
                  <el-option :label="$t('general.inactive')" :value="0" />
                </el-select>
              </div>
              <div class="form-group challenge-campaigns-page__search-btn">
                <button
                  class="btn add"
                  type="button"
                  @click="applyFilters"
                  :title="$t('reports.search')"
                >
                  <i class="ti-search"></i> {{ $t('general.search') }}
                </button>
              </div>
            </div>
            <div class="challenge-campaigns-page__add">
              <router-link
                class="btn add"
                :title="$t('general.add') + ' ' + $t('challengeCampaigns.single')"
                :to="{ path: '/marketing/challenge-campaigns/add' }"
              >
                <img src="/static/img/icons/icon-plus-sm.png">
                {{ $t('general.add') }}
              </router-link>
            </div>
          </div>

          <div class="data-table">
            <el-table
              :data="filteredTableData"
              :empty-text="$t('gestiune.noData')"
              style="width: 100%;"
              @row-click="goToRecord"
            >
              <el-table-column prop="id" :label="$t('challengeCampaigns.id')" width="70" />
              <el-table-column :label="$t('challengeCampaigns.image')" width="90">
                <template slot-scope="scope">
                  <div class="image-table" v-if="scope.row.image">
                    <img :src="scope.row.image" alt="">
                  </div>
                  <span v-else>—</span>
                </template>
              </el-table-column>
              <el-table-column prop="title" :label="$t('challengeCampaigns.title')" min-width="140" />
              <el-table-column :label="$t('challengeCampaigns.description')" min-width="200">
                <template slot-scope="scope">
                  {{ scope.row.description || '—' }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('challengeCampaigns.target')" width="90">
                <template slot-scope="scope">
                  {{ scope.row.target }}
                </template>
              </el-table-column>
              <el-table-column :label="$t('general.active')" width="100">
                <template slot-scope="scope">
                  <div @click.stop>
                    <p-switch
                      v-model="scope.row.active"
                      type="success"
                      @input="handleActive(scope.row)"
                    />
                  </div>
                </template>
              </el-table-column>
              <el-table-column :label="$t('general.actions')" width="120">
                <template slot-scope="scope">
                  <el-dropdown trigger="click" placement="bottom-end" @click.native.stop>
                    <span class="arrow-box">&#9662;</span>
                    <el-dropdown-menu slot="dropdown" class="custom-dropdown-menu">
                      <el-dropdown-item @click.native.stop="goToRecord(scope.row)">
                        {{ $t('general.edit') }}
                      </el-dropdown-item>
                      <el-dropdown-item @click.native.stop="handleDelete(scope.row)">
                        {{ $t('general.delete') }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </template>
              </el-table-column>
            </el-table>

            <el-pagination
              layout="prev, pager, next, sizes, total"
              :total="filteredAll.length"
              :page-size="paginationDef.pageSize"
              :page-sizes="paginationDef.pageSizes"
              :current-page.sync="paginationDef.currentPage"
              @size-change="onPageSizeChange"
              style="margin-top: 15px; text-align: center;"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { Input, Select, Option, Dropdown, DropdownMenu, DropdownItem, Pagination } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'

Vue.use(Input)
Vue.use(Select)
Vue.use(Option)
Vue.use(Dropdown)
Vue.use(DropdownMenu)
Vue.use(DropdownItem)
Vue.use(Pagination)

export default {
  components: { PSwitch },
  props: {
    isChallengeCampaigns: { type: Boolean, default: false }
  },
  data () {
    return {
      loading: true,
      search: '',
      appliedSearch: '',
      statusFilter: null,
      appliedStatus: null,
      tableData: [],
      paginationDef: {
        pageSize: 20,
        pageSizes: [10, 20, 50],
        currentPage: 1
      }
    }
  },
  computed: {
    filteredAll () {
      let rows = this.tableData || []
      const q = (this.appliedSearch || '').toString().trim().toLowerCase()
      if (q) {
        rows = rows.filter(r => {
          const title = (r.title || '').toString().toLowerCase()
          const desc = (r.description || '').toString().toLowerCase()
          return title.indexOf(q) !== -1 || desc.indexOf(q) !== -1
        })
      }
      if (this.appliedStatus === 0 || this.appliedStatus === 1) {
        rows = rows.filter(r => !!r.active === !!this.appliedStatus)
      }
      return rows
    },
    filteredTableData () {
      const start = (this.paginationDef.currentPage - 1) * this.paginationDef.pageSize
      return this.filteredAll.slice(start, start + this.paginationDef.pageSize)
    }
  },
  watch: {
    isChallengeCampaigns (v) {
      if (v) this.getCampaigns()
    }
  },
  created () {
    this.getCampaigns()
    this.$bus.$on('refresh', this.getCampaigns)
  },
  beforeDestroy () {
    this.$bus.$off('refresh', this.getCampaigns)
  },
  methods: {
    applyFilters () {
      this.appliedSearch = this.search
      this.appliedStatus = this.statusFilter
      this.paginationDef.currentPage = 1
    },
    onPageSizeChange (size) {
      this.paginationDef.pageSize = size
      this.paginationDef.currentPage = 1
    },
    getCampaigns () {
      this.loading = true
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/challenge-campaigns', 'GET', data, (response) => {
        this.loading = false
        if (response.status) {
          this.tableData = (response.data || []).map(row => {
            row.active = !!(row.active === true || parseInt(row.active) === 1)
            return row
          })
        }
      })
    },
    goToRecord (row) {
      if (!row || !row.id) return
      this.$router.push('/marketing/challenge-campaigns/edit/' + row.id)
    },
    handleActive (row) {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        active: row.active ? 1 : 0
      }
      this.apiRequest('/challenge-campaigns/' + row.id, 'PUT', data, (response) => {
        if (!response.status) {
          row.active = !row.active
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        }
      })
    },
    handleDelete (row) {
      swal({
        text: this.$t('challengeCampaigns.areYouSureDelete') + ' ' + (row.title || '') + '?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes')
      }).then((result) => {
        if (!result) return
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/challenge-campaigns/' + row.id, 'DELETE', data, (response) => {
          if (response.status) this.getCampaigns()
          else swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        })
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.challenge-campaigns-page__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.challenge-campaigns-page__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
  flex: 1 1 auto;
}
.challenge-campaigns-page__search {
  flex: 1 1 220px;
  margin: 0;
}
.challenge-campaigns-page__status {
  flex: 0 0 160px;
  margin: 0;
}
.challenge-campaigns-page__search-btn {
  flex: 0 0 auto;
  margin: 0;
}
.challenge-campaigns-page__search-btn .btn,
.challenge-campaigns-page__add .btn {
  font-size: 12px;
  padding: 4px 8px;
}
.challenge-campaigns-page__add {
  flex: 0 0 auto;
  margin-bottom: 4px;
}
.challenge-campaigns-page .image-table {
  width: 56px;
  height: 56px;
  padding: 8px;
}
.challenge-campaigns-page .image-table img {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}
</style>

<style>
.challenge-campaigns-page .arrow-box {
  display: inline-block;
  font-size: 18px;
  padding: 4px 6px;
  border: 1px solid #674BD2 !important;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  user-select: none;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
  color: #674BD2;
}
.challenge-campaigns-page .arrow-box:hover {
  background-color: #674BD2 !important;
  border-color: #674BD2 !important;
  color: #fff !important;
}
.challenge-campaigns-page .custom-dropdown-menu {
  min-width: 180px;
  font-size: 14px;
}
.challenge-campaigns-page .el-pagination {
  text-align: center !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/ChallengeCampaigns/ChallengeCampaigns.vue